import React, { useState } from 'react';
import { sportsApi } from '../../services/sportsApi';
import { Season, Club } from '../../types/sports';

interface CreateOwnMatchModalProps {
  seasons: Season[];
  clubs: Club[];
  defaultSeasonId: number | null;
  preferredClubId?: number;
  onClose: () => void;
  onCreated: () => void;
}

const CreateOwnMatchModal: React.FC<CreateOwnMatchModalProps> = ({
  seasons, clubs, defaultSeasonId, preferredClubId, onClose, onCreated,
}) => {
  const [matchType, setMatchType] = useState<number>(1); // 1 = Testspiel, 2 = Training
  const [scheduledDate, setScheduledDate] = useState('');
  const [seasonId, setSeasonId] = useState<number | ''>(defaultSeasonId ?? '');
  const [isHome, setIsHome] = useState(true);
  const [ownClubId, setOwnClubId] = useState<number | ''>(preferredClubId ?? '');
  const [ownTeamName, setOwnTeamName] = useState('');
  const [opponentClubId, setOpponentClubId] = useState<number | ''>('');
  const [opponentTeamName, setOpponentTeamName] = useState('');
  const [location, setLocation] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!scheduledDate) { setError('Bitte Datum und Uhrzeit angeben.'); return; }
    if (!seasonId) { setError('Bitte eine Saison wählen.'); return; }
    if (!ownClubId && !ownTeamName.trim()) { setError('Bitte das eigene Team angeben.'); return; }
    if (matchType === 1 && !opponentClubId && !opponentTeamName.trim()) {
      setError('Für ein Testspiel ist ein Gegner erforderlich.'); return;
    }
    try {
      setSubmitting(true);
      await sportsApi.createOwnMatch({
        seasonId: Number(seasonId),
        matchType,
        scheduledDate,
        isHome,
        ownClubId: ownClubId ? Number(ownClubId) : undefined,
        ownTeamName: ownTeamName.trim() || undefined,
        opponentClubId: opponentClubId ? Number(opponentClubId) : undefined,
        opponentTeamName: opponentTeamName.trim() || undefined,
        location: location.trim() || undefined,
        locationAddress: locationAddress.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onCreated();
    } catch (err) {
      console.error('Error creating own match:', err);
      setError('Fehler beim Anlegen des Spiels.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Eigenes Spiel anlegen</h3>
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        </div>
        <form onSubmit={handleSubmit} style={formStyle}>
          <label style={labelStyle}>Art
            <select value={matchType} onChange={e => setMatchType(Number(e.target.value))} style={inputStyle}>
              <option value={1}>Testspiel</option>
              <option value={2}>Training</option>
            </select>
          </label>
          <label style={labelStyle}>Datum & Uhrzeit
            <input type="datetime-local" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} style={inputStyle} />
          </label>
          <label style={labelStyle}>Saison
            <select value={seasonId} onChange={e => setSeasonId(e.target.value ? Number(e.target.value) : '')} style={inputStyle}>
              <option value="">Bitte wählen</option>
              {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label style={labelStyle}>Heim / Auswärts
            <select value={isHome ? 'home' : 'away'} onChange={e => setIsHome(e.target.value === 'home')} style={inputStyle}>
              <option value="home">Heim</option>
              <option value="away">Auswärts</option>
            </select>
          </label>
          <label style={labelStyle}>Eigenes Team (Verein)
            <select value={ownClubId} onChange={e => setOwnClubId(e.target.value ? Number(e.target.value) : '')} style={inputStyle}>
              <option value="">— Freitext unten —</option>
              {clubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          {!ownClubId && (
            <input placeholder="Eigenes Team (Freitext)" value={ownTeamName} onChange={e => setOwnTeamName(e.target.value)} style={inputStyle} />
          )}
          <label style={labelStyle}>Gegner (Verein)
            <select value={opponentClubId} onChange={e => setOpponentClubId(e.target.value ? Number(e.target.value) : '')} style={inputStyle}>
              <option value="">— Freitext unten —</option>
              {clubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          {!opponentClubId && (
            <input placeholder={matchType === 1 ? 'Gegner (Freitext)' : 'Gegner (optional)'} value={opponentTeamName} onChange={e => setOpponentTeamName(e.target.value)} style={inputStyle} />
          )}
          <input placeholder="Ort (z. B. Trainingsplatz)" value={location} onChange={e => setLocation(e.target.value)} style={inputStyle} />
          <input placeholder="Adresse" value={locationAddress} onChange={e => setLocationAddress(e.target.value)} style={inputStyle} />
          <textarea placeholder="Notizen" value={notes} onChange={e => setNotes(e.target.value)} style={{ ...inputStyle, minHeight: 60 }} />
          {error && <div style={{ color: '#e74c3c', fontSize: 13 }}>{error}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button type="button" onClick={onClose} className="btn btn-secondary" disabled={submitting}>Abbrechen</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Speichern…' : 'Anlegen'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const overlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const modalStyle: React.CSSProperties = { background: 'white', borderRadius: 12, padding: 20, width: 440, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' };
const modalHeaderStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 };
const closeBtnStyle: React.CSSProperties = { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#666' };
const formStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8 };
const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: '#333' };
const inputStyle: React.CSSProperties = { padding: '6px 8px', border: '1px solid #dee2e6', borderRadius: 6, fontSize: 14, fontWeight: 400 };

export default CreateOwnMatchModal;
